import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Input,
  notification,
  Typography,
  Form
} from 'antd';
import {
  LockOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { httpService } from '../../services/httpService';
import { useTheme } from '../../theme/ThemeProvider';
import './styles.css';

const { Title, Text } = Typography;

const OTPVerification: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const phoneNumber = location.state?.phoneNumber;
  const [form] = Form.useForm();
  const { theme } = useTheme();

  useEffect(() => {
    if (!phoneNumber) {
      navigate('/');
      return;
    }

    // Start countdown for resend
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [phoneNumber, navigate]);

  const handleSubmit = async (values: { otp: string }) => {
    const { otp } = values;

    if (!otp || otp.length !== 6) {
      notification.error({ message: 'لطفا کد 6 رقمی را وارد کنید' });
      return;
    }

    setIsLoading(true);
    try {
      // User exists, proceed with login directly
      await httpService.registerAndLogin({
        mobileNumber: phoneNumber,
        username: '',
        password: otp,
        remember: false,
        isOTP: true
      });
      notification.success({ message: 'ورود موفقیت‌آمیز بود' });
      navigate('/home');
    } catch (error) {
      notification.error({ message: 'کد وارد شده صحیح نیست' });
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) return;

    setIsLoading(true);
    try {
      await httpService.sendOTP({
        mobileNumber: phoneNumber,
        remember: false,
        isOTP: true
      });
      notification.success({ message: 'کد تایید مجدد ارسال شد' });
      setCountdown(60);
    } catch (error) {
      notification.error({ message: 'خطا در ارسال مجدد کد' });
      console.error('Resend OTP error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/');
  };

  return (
    <div className="otp-container" style={{ background: theme.background }}>
      <div className="otp-content">
        <Card 
          className="card-themed"
          style={{
            backgroundColor: theme.surface,
            borderColor: theme.border,
            boxShadow: `0 4px 12px rgba(0, 0, 0, 0.1)`
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <Title level={2} style={{ marginBottom: 8, color: theme.text.primary }}>
              کد تایید
            </Title>
            <Text type="secondary" style={{ fontSize: 16, color: theme.text.secondary }}>
              کد ارسال شده به شماره {phoneNumber} را وارد کنید
            </Text>
          </div>

          <Form 
            form={form}
            layout="vertical" 
            onFinish={handleSubmit}
            size="large"
          >
            <Form.Item 
              label="کد تایید"
              name="otp"
              rules={[
                { required: true, message: 'لطفا کد تایید را وارد کنید' },
                { len: 6, message: 'کد تایید باید ۶ رقم باشد' }
              ]}
            >
              <Input
                placeholder="کد 6 رقمی"
                maxLength={6}
                style={{ 
                  textAlign: 'center', 
                  fontSize: 18, 
                  letterSpacing: 8,
                  direction: 'ltr'
                }}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                size="large"
                block
                loading={isLoading}
                htmlType="submit"
                icon={<LockOutlined />}
              >
                تایید
              </Button>
            </Form.Item>

            <Form.Item style={{ marginBottom: 16 }}>
              <Button
                type="link"
                block
                disabled={countdown > 0}
                onClick={handleResendOTP}
                style={{ padding: 0 }}
              >
                {countdown > 0 ? `ارسال مجدد (${countdown})` : 'ارسال مجدد کد'}
              </Button>
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="text"
                block
                icon={<ArrowLeftOutlined />}
                onClick={handleBackToLogin}
                style={{ color: theme.text.secondary }}
              >
                بازگشت به صفحه ورود
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default OTPVerification; 